import Vue from 'vue';
import Router from 'vue-router';
import ViewUI from 'view-design';
import routes from './routes';
import config from '@/config';
import {
    clearLoginStatus,
    isNotLoginPage,
    canTurnTo,
    localSave,
    localRead,
    localRemove,
    sessionSave,
    sessionRemove,
    isSSO,
    getMenuByRouter,
    hasAccess,
} from '@/libs/util';
import { NO_LOGOUT_PAGE, NO_SAVE_LOCAL_NAV, THIRD_PARTY_SSO_K_I } from '@/constants/common';
import { loadLanguageAsync, getLang, initLanguage } from '@/locales';
import { keepBackParamsRouterMap } from '@/config/constant';

const {
    tokenHomeName,
    authHomeName,
    title,
    siteConfig,
} = config;

const getName = (access: any): string => {
    let goName: string = '';
    if (!access) {
        return '';
    }
    routes.map((item: any) => {
        if (item.children && item.children.length && !goName) {
            item.children.map((child: any) => {
                const isAuth = canTurnTo(child.name, access, routes);
                if (child.meta.access && isAuth && !goName) {
                    goName = child.name;
                }
                return child;
            });
        }
        return item;
    });
    return goName;
};

export const whereToGo = (userId: string, access: any, mode: string = 'TOKEN_MODE') => {
    const authName = getName(access) || (mode === 'TOKEN_MODE' ? tokenHomeName : authHomeName);
    const lastNav = localRead(`lastNav_${userId}`);
    const lastNavParams = JSON.parse(localRead(`lastNavParams_${userId}`) || '{}');
    if (lastNav && canTurnTo(lastNav, access, routes)) {
        return {
            name: lastNav,
            params: lastNavParams,
        };
    }
    return {
        name: authName,
        params: {},
    };
};

const getMenuList = (menuList: any[], access: string[], isCaiNiao: boolean) => {
    let list: any[] = [];
    // 无二级菜单
    const specialMenus = ['authMode_chat', 'channels', 'authMode_setting', 'appstore'];
    if (!isCaiNiao) {
        specialMenus.push('fee');
    }
    menuList.forEach((item: any) => {
        const child = getMenuByRouter(item.children || [], access);
        if (specialMenus.includes(item.name)) {
            list.push(item);
        } else if (item.children && child.length) {
            if (!['authMode_reward', 'helpdesk'].includes(item.name)) {
                list = list.concat(child);
            }
        }
    });
    return list.map((x) => x.name);
};

Vue.use(Router);

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
});

const turnTo = ({
    to,
    from,
    access,
    next,
    userId,
    systemMode,
    isNewUi,
}: {
    to: any,
    from: any,
    access: any,
    next: any,
    userId: string,
    systemMode: string,
    isNewUi?: '0' | '1' | '2' | '3',
}) => {
    if (canTurnTo(to.name, access, routes)) {
        if (isSSO()) {
            next();
            return true;
        }

        // 有权限，可访问
        const lastNav = whereToGo(userId, access, systemMode);
        if (to.name === 'home') {
            console.log('router-to-home');
            next({
                replace: true,
                name: lastNav.name,
                params: lastNav.params,
            });
        } else if (to.meta.notPageView) {
            // 如果进入的是第一层路由，自动跳转到第一个有权限的子路由
            let routerInfo: any = routes;
            to.matched.forEach((matchedItem: any) => {
                const match = routerInfo.find((item: any) => matchedItem.name === item.name);
                if (match && match.children && Array.isArray(match.children)) {
                    routerInfo = match.children || [];
                }
            });

            const nextPage = routerInfo.find((item: any) => hasAccess(access, item));
            if (nextPage) {
                next({
                    replace: true,
                    name: nextPage.name,
                    params: to.params,
                });
            } else {
                next({ replace: true, name: 'error401' });
            }
        } else {
            // eslint-disable-next-line no-prototype-builtins
            const isTargetRoute = keepBackParamsRouterMap.hasOwnProperty(to.name);
            const isFromRoute = keepBackParamsRouterMap[to.name]?.includes(from.name) || false;
            if (from.name && isTargetRoute && isFromRoute && from.params?.ss_source_back_data) {
                to.params.ss_target_back_data = from.params?.ss_source_back_data;
            }

            // 非白牌，跳转新版(这里只能拦截从其他界面过来的时候)
            // 兜底拦截在 src/views/main/authMode/Chat/index.vue
            if (['authMode_chat'].includes(to.name) && (isNewUi === '2' || isNewUi === '3') && !siteConfig.brand) {
                // 跳转新版
                const { href } = router.resolve(to);
                window.location.href = `/next${href}`;
            } else {
                next();
            }
        }
    } else {
        const lastLocalNav = localRead(`lastNav_${userId}`);
        const vm: any = router.app;
        const menuList = vm.$options.store.getters['main/menuList'];
        const isCaiNiao = vm.$options.store.getters['main/isCaiNiao'];
        const sideMenuList = getMenuList(menuList, access, isCaiNiao);

        // 首页 或者是401页面的返回上一页，判断是否有权限
        if ((['home', 'index_home'].includes(to.name as string) && sideMenuList && sideMenuList.length) || !lastLocalNav) {
            // 跳转到第一个有权限的页面
            const name = sideMenuList[0];
            next({
                replace: true,
                name,
                params: {},
            });
            return true;
        }

        // 无权限，重定向到401页面
        next({ replace: true, name: 'error401' });
    }
    return true;
};

const getGrayReleaseUrl = (projectIdOrUserId: string, data: any): string => {
    if (!projectIdOrUserId || !data) {
        return '';
    }
    let baseUrlRes = '';
    for (const baseUrl in data) {
        if (baseUrl && data[baseUrl]) {
            const projectIds = data[baseUrl].company_ids;
            for (const projectId of projectIds) {
                if (`${projectIdOrUserId}` === `${projectId}`) {
                    baseUrlRes = baseUrl;
                    break;
                }
            }
            const userIds = data[baseUrl].user_ids;
            for (const userId of userIds) {
                if (`${projectIdOrUserId}` === `${userId}`) {
                    baseUrlRes = baseUrl;
                    break;
                }
            }
        }
    }
    if (baseUrlRes && baseUrlRes.charAt(baseUrlRes.length - 1) !== '/') {
        baseUrlRes = `${baseUrlRes}/`;
    }
    return baseUrlRes;
};

const LOGIN_PAGE_NAME = 'login';

router.beforeEach((to, from, next) => {
    const toPath = to?.fullPath || from?.fullPath;
    window.onunhandledrejection = (event: any) => {
        if (event && event.reason && event.reason.message && event.reason.message.includes('Failed to fetch dynamically imported module')) {
            const expired = localRead('UNHANDLE_REJECTION');
            if (!expired || Date.now() - +expired > 60 * 1000) {
                window.onunhandledrejection = null;
                localSave('UNHANDLE_REJECTION', Date.now().toString());
                window.location.href = toPath;
            }
        }
    };

    // 记录营销参数
    let hasRemktSource = false;
    if (to.query.remkt_source) {
        sessionSave('REMKT_SOURCE', to.query.remkt_source as string);
        sessionSave('REMKT_SOURCE_URL', window.location.href);
        hasRemktSource = true;
        delete to.query.remkt_source;
    }

    const language =
        ['registration', 'login'].includes(to.name || '') && to.query.lang ?
            initLanguage(to.query.lang as string) :
            getLang();
    loadLanguageAsync(language).finally(() => {
        if (hasRemktSource) {
            next({
                path: to.path,
                query: to.query,
                params: to.params || {},
                hash: to.hash,
                replace: true,
            });
        } else {
            next();
        }
    });

    ViewUI.LoadingBar.finish();
});

router.beforeEach((to, from, next) => {
    if (to.name === 'authSSO') {
        localRemove(config.tokenKey);
        sessionRemove(THIRD_PARTY_SSO_K_I);
        next();
        return;
    }

    // ViewUI.LoadingBar.start();

    // 白牌隐藏路由重定向
    try {
        if (siteConfig.brand && siteConfig?.hideMenu && siteConfig.hideMenu.includes(to.name)) {
            next({
                name: 'authMode_chat',
            });
            return;
        }
    } catch (e) {
        console.error('siteConfig error', e);
    }

    const vm: any = router.app;
    const token = localRead(config.tokenKey);
    const query = to.query || {};
    // 不能执行"用户登出"的页面
    if (NO_LOGOUT_PAGE.includes(to.name as string) || to.params.token || (window as any).SS_ACTIVITY) {
        next();
        return;
    }

    // 针对wordpress跳转做特殊处理
    if (to.name === 'login' && query.source === 'wordpress') {
        if (query.type && ['authorized_shop', 'unauthorized_shop'].includes(query.type as string)) {
            vm.$options.store.dispatch('user/passport/passiveLogout');
            next();
        } else {
            // 跳转中转页判断店铺绑定状态
            next({
                name: 'authShop',
                query,
            });
        }
        return;
    }

    if (!token && isNotLoginPage(to.name as string)) {
        if (to.name === 'payresult') {
            next();
            return;
        }
        vm.$options.store.dispatch('user/passport/passiveLogout');
        next();
        return;
    }
    const { access, userId, isNewUi } = vm.$options.store.state.user;
    let systemMode = vm.$options.store.state.user.systemMode;
    if (userId) {
        if (isNotLoginPage(to.name as string)) {
            const lastNav = whereToGo(userId, access, systemMode);

            if (isSSO()) {
                next();
                return;
            }

            next({
                name: lastNav.name,
                params: lastNav.params,
            });
            return;
        }
        turnTo({
            to,
            from,
            access,
            next,
            userId,
            systemMode,
            isNewUi,
        });
    } else if (to.name === 'noProject') {
        // 如果已经在 noProject 页面，不再请求 getUserInfo
        next();
    } else {
        vm.$options.store.dispatch('user/profile/getUserInfo').then((res: any) => {
            systemMode = vm.$options.store.state.user.systemMode; // 获取信息之后再读取一次

            if (isNotLoginPage(to.name as string)) {
                const lastNav = whereToGo(res.data.id, res.data.auth_list, systemMode);
                next({
                    name: lastNav.name,
                    params: lastNav.params,
                });
                return;
            }
            // 如果projectId 是 0 则表示没有项目
            if (Number(vm.$options.store.state.user.projectId) === 0) {
                next({
                    name: 'noProject',
                });
                return;
            }

            turnTo({
                to,
                from,
                access: res.data.auth_list,
                next,
                userId: res.data.id,
                systemMode,
                isNewUi,
            });
        }).catch(() => {
            vm.$options.store.dispatch('user/passport/passiveLogout');
            clearLoginStatus();
            if (isNotLoginPage(to.name as string)) {
                next();
            } else {
                if (to.name !== 'home' && !isNotLoginPage(to.name as string)) {
                    query.url = escape(window.location.href);
                }
                next({
                    name: LOGIN_PAGE_NAME, // 跳转到登录页
                    query,
                });
            }
        });
    }
});

router.afterEach((to, from) => {
    window.onunhandledrejection = null;

    const vm: any = router.app;
    const { name, params } = to;
    if ((window as any).SS_ACTIVITY) {
        document.title = 'SaleSmartly';
    } else {
        const routeName = vm.$t(`nav.${to.name}`);
        let titleText = `${title}`;
        const envTxt = process.env.VUE_APP_CURRENTMODE || process.env.NODE_ENV;
        titleText += ` | ${routeName}`;
        if (envTxt && envTxt !== 'pro' && envTxt !== 'production') {
            titleText += `【${envTxt}】`;
        }
        document.title = titleText;
    }
    ViewUI.LoadingBar.finish();
    window.scrollTo(0, 0);
    const { userId } = vm.$options.store.state.user;
    if (name && !isNotLoginPage(name) && !NO_SAVE_LOCAL_NAV.includes(name)) {
        localSave(`lastNav_${userId}`, name);
        localSave(`lastNavParams_${userId}`, JSON.stringify(params));
    }
});

export default router;
